import React, { useCallback, useEffect, createRef } from 'react';
import { DocumentNode, ExecutionResult } from 'graphql';
import { useQuery, useMutation } from '@apollo/client';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { useForm } from 'hooks/useForm';
import { parseQueryParams, buildSearchQueryParams } from 'utilities/query-params-util';
import PageContainer from '../../@layout/PageContainer';
import ResourcesFilterBar from './components/ResourcesFilterBar';
import ResourceCard from './components/ResourceCard';
import GET_RESOURCES from 'graph/resource/resources-with-assets-query';
import GET_RESOURCE_FAVORITES from 'graph/favorite/resource-favorites-query';
import CREATE_RESOURCE_FAVORITE from 'graph/favorite/create-resource-favorite-mutation';
import DELETE_RESOURCE_FAVORITE from 'graph/favorite/delete-resource-favorite-mutation';
import { updateCache } from 'graph/update-cache';
import {
  filterResources,
  isFavorites,
  getSearchBarSelectOptions,
  getResourceFavorite,
  withFavorite,
  FAVORITES
} from '../util';
import { ResourceFilterBarFormState, DefaultOption, ResourcesParams } from '../types';
import { ModuleProps } from 'types';
import { Resource, ResourceFavorite } from 'types/schema-generated-types';
import EmptyResultSetMessage from 'components/@common/EmptyResultSetMessage';
import HeaderBar from '../HeaderBar';
import { useRouteMatch, useHistory } from 'react-router-dom';
import Config from 'config';

interface ResourceQueryPayload {
  query: DocumentNode;
  variables: { [key: string]: any };
  propName: string;
}
const getResourcesQuery = (resourceTypeId: string | number): ResourceQueryPayload => {
  if (isFavorites(resourceTypeId)) {
    return {
      query: GET_RESOURCE_FAVORITES,
      variables: {},
      propName: 'resourceFavorites'
    };
  }

  return {
    query: GET_RESOURCES,
    variables: { where: { resourceTypeId } },
    propName: 'resources'
  };
};

const getDeleteFavoriteMutationCacheUpdates = (): any => ({
  update: updateCache<Resource[]>([
    {
      query: GET_RESOURCE_FAVORITES,
      variables: {},
      reducer: (cachedData, { deleteResourceFavorite }): { resourceFavorites: Resource[] } => {
        return {
          resourceFavorites: cachedData
            ? cachedData.resourceFavorites.filter(x => x.id !== deleteResourceFavorite.resourceId)
            : []
        };
      }
    }
  ])
});

const getCreateFavoriteMutationUpdates = (entities: Resource[]): any => ({
  update: updateCache<Resource[]>([
    {
      query: GET_RESOURCE_FAVORITES,
      variables: {},
      reducer: (cachedData, { createResourceFavorite }): { resourceFavorites: Resource[] } => {
        const resource = entities.find(x => x.id === createResourceFavorite.resourceId);

        return {
          resourceFavorites: cachedData
            ? cachedData.resourceFavorites.concat({
                ...(resource as Resource),
                favorite: createResourceFavorite
              })
            : []
        };
      }
    }
  ])
});

const useStyles = makeStyles((theme: Theme) => {
  return {
    container: {
      display: 'grid',
      gridGap: 24,
      gridTemplateColumns: `repeat(auto-fill, minmax(550px, 1fr))`,
      gridAutoFlow: 'dense',
      padding: `${theme.spacing(3)}px ${theme.spacing(5)}px`
    }
  };
});

export const getInitialSearchBarFormState = (): ResourceFilterBarFormState => ({
  resource: DefaultOption.ALL,
  product: DefaultOption.ALL,
  grade: DefaultOption.ALL,
  search: ''
});

const Resources: React.FC<ModuleProps> = ({ id, getTitle, ...props }) => {
  const classes = useStyles();
  const { t } = useTranslation(id);
  const match = useRouteMatch<ResourcesParams>();
  const history = useHistory();
  const searchInputRef = createRef<HTMLInputElement>();
  const resourceTypeId =
    match.params?.resourceTypeId === FAVORITES ? FAVORITES : +match.params?.resourceTypeId;
  const [state, { setForm, onFormChange }] = useForm(getInitialSearchBarFormState());
  const { data: { resourceFavorites = [] } = {} }: any = useQuery(GET_RESOURCE_FAVORITES);
  const getQueryDetails = useCallback(() => getResourcesQuery(resourceTypeId), [resourceTypeId]);
  const { query, variables, propName } = getQueryDetails();
  const {
    loading,
    error,
    data: { [propName]: entities = [] as Resource[] } = {} as { [key: string]: Resource[] }
  } = useQuery(query, {
    variables
  });

  const [createResourceFavorite] = useMutation(
    CREATE_RESOURCE_FAVORITE,
    getCreateFavoriteMutationUpdates(entities)
  );
  const [deleteFavorite] = useMutation(
    DELETE_RESOURCE_FAVORITE,
    getDeleteFavoriteMutationCacheUpdates()
  );

  const selectOptions = useCallback(
    () => getSearchBarSelectOptions(resourceTypeId, entities, { t }),
    [entities, resourceTypeId, t]
  );
  const filteredEntites = useCallback(
    () => withFavorite(resourceFavorites, filterResources(state.form, entities, resourceFavorites)),
    [state.form, entities, resourceFavorites]
  );

  useEffect(() => {
    const queryParams = parseQueryParams(history.location?.search);
    if (queryParams) {
      const { activeSearch, ...params } = queryParams;

      setForm({
        ...getInitialSearchBarFormState(),
        ...params
      });

      if (activeSearch || params.search || state.form.search) {
        searchInputRef?.current?.focus();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTileClick = (resource: Resource) => (): void => {
    if (resource.link && resource.link.includes(Config.appUrl)) {
      const url = resource.link.replace(`${Config.appUrl}`, '');
      history.push(`/${url}`);
    } else {
      window.open(resource.resourceAsset?.url || (resource.link as string), '_blank');
    }
  };

  const handleFilterBarChange = (name: string, value: string): void => {
    const optimisticEntity = onFormChange(name, value);
    /**
     * Not live tracking 'search' in 'url' as this would require more complicated solution; however, 'search' in url does populate search field correctly
     */
    if (name !== 'search') {
      history.push(
        `${history.location.pathname}${buildSearchQueryParams({
          ...optimisticEntity
        })}`
      );
    }
  };

  const handleClearFilterBar = (): void => {
    const initialState = getInitialSearchBarFormState();

    setForm(initialState);
    history.push(`${history.location.pathname}`);
  };

  const handleFavoriteClick = (resource: Resource) => async (): Promise<
    ExecutionResult<ResourceFavorite>
  > => {
    const resourceFavorite = getResourceFavorite(resourceFavorites, resource);
    return resourceFavorite
      ? deleteFavorite({ variables: { where: { favoriteId: resourceFavorite?.favorite?.id } } })
      : createResourceFavorite({ variables: { data: { resourceId: resource.id } } });
  };

  const memoizedEntities = filteredEntites();

  return (
    <PageContainer loading={loading} error={error}>
      <HeaderBar {...props} id={id} getTitle={getTitle} />
      <ResourcesFilterBar
        {...selectOptions()}
        {...{ ref: searchInputRef }}
        form={state.form}
        handleChange={handleFilterBarChange}
        handleClearFilters={handleClearFilterBar}
      />

      {memoizedEntities.length ? (
        <div className={classes.container}>
          {memoizedEntities.map((x: any, idx: number) => (
            <ResourceCard
              key={`resource_tile_card_${idx}`}
              handleCardClick={handleTileClick(x)}
              handleFavoriteClick={handleFavoriteClick(x)}
              icon={x.type.iconAsset.url}
              thumbnail={x.thumbnailAsset.url}
              {...(x as Partial<Resource>)}
            />
          ))}
        </div>
      ) : (
        <EmptyResultSetMessage message={'resources'} />
      )}
    </PageContainer>
  );
};

export default Resources;
